import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { scorecardsApi, type NivelIndicador, type Tendencia, type LinhaPainel } from '../api/scorecards.js';
import { ChipStatus } from '../componentes/transversais/ChipStatus.js';
import { VazioQueEnsina } from '../componentes/transversais/VazioQueEnsina.js';
import { SerieHistorica } from '../componentes/scorecards/SerieHistorica.js';

const SETA_TENDENCIA: Record<Tendencia, string> = {
  Melhorando: '↑',
  Estavel: '→',
  Piorando: '↓',
};

const NIVEIS: NivelIndicador[] = ['Projeto', 'Programa', 'Portfolio'];

function LinhaIndicador({ linha, expandido, onAlternar }: { linha: LinhaPainel; expandido: boolean; onAlternar: () => void }) {
  const { data: medicoes } = useQuery({
    queryKey: ['indicador', linha.indicador.id, 'medicoes'],
    queryFn: () => scorecardsApi.medicoes(linha.indicador.id),
    enabled: expandido,
  });

  return (
    <li className="rounded-lg border border-borda bg-superficie p-4">
      <button type="button" onClick={onAlternar} className="flex w-full items-center justify-between text-left">
        <div>
          <p className="text-sm font-medium text-ink">{linha.indicador.nome}</p>
          <p className="text-xs text-grafite">Dono: {linha.indicador.dono}</p>
        </div>
        <div className="flex items-center gap-3">
          {linha.tendencia && <span className="fonte-mono text-sm text-grafite">{SETA_TENDENCIA[linha.tendencia]}</span>}
          {linha.status && linha.valor !== null && linha.calculadoEm ? (
            <ChipStatus
              status={linha.status}
              formula={linha.indicador.formula}
              fonte={linha.indicador.fonteDado}
              valor={linha.valor}
              meta={linha.indicador.meta}
              fresco={linha.fresco}
              calculadoEm={linha.calculadoEm}
            />
          ) : (
            <span className="text-xs text-grafite">sem medição</span>
          )}
          <span className="fonte-mono text-xs text-grafite">{expandido ? '▾' : '▸'}</span>
        </div>
      </button>

      {expandido && (
        <div className="mt-4 border-t border-borda pt-3">
          {medicoes ? (
            <SerieHistorica medicoes={medicoes} meta={linha.indicador.meta} baseline={linha.indicador.baseline} />
          ) : (
            <p className="text-xs text-grafite">Carregando série…</p>
          )}
        </div>
      )}
    </li>
  );
}

// SPEC-UX §4.10 — painel por nível com os indicadores do catálogo E9; cada
// um é o ChipStatus (§3.4) expandível em série histórica com baseline e
// meta (linha tracejada), tendência ao lado do status (seta — R9.3). URL
// permanente por snapshot de data fica fora — exigiria versionamento de
// rota que não faz parte desta fatia.
export function Scorecards() {
  const [nivel, setNivel] = useState<NivelIndicador>('Portfolio');
  const [contextoId, setContextoId] = useState('');
  const [indicadorExpandido, setIndicadorExpandido] = useState<string | null>(null);

  const consultaAtiva = contextoId.trim().length > 0;
  const { data: linhas, isLoading } = useQuery({
    queryKey: ['scorecards', nivel, contextoId],
    queryFn: () => scorecardsApi.painel(nivel, contextoId),
    enabled: consultaAtiva,
  });

  return (
    <div className="flex flex-col gap-6 p-8">
      <h1 className="text-xl font-semibold text-ink">Scorecards</h1>

      <div className="flex gap-2">
        <select value={nivel} onChange={(e) => setNivel(e.target.value as NivelIndicador)} className="rounded border border-borda bg-base-100 px-2 py-1.5 text-sm text-ink">
          {NIVEIS.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
        <input
          placeholder="ID do contexto"
          value={contextoId}
          onChange={(e) => setContextoId(e.target.value)}
          className="flex-1 rounded border border-borda bg-base-100 px-3 py-1.5 text-sm text-ink"
        />
      </div>

      {!consultaAtiva && <VazioQueEnsina explicacao="Informe o nível e o ID do contexto (projeto, programa ou portfólio) para ver o painel de indicadores." />}

      {isLoading && <p className="text-sm text-grafite">Carregando…</p>}

      {consultaAtiva && !isLoading && (!linhas || linhas.length === 0) && (
        <VazioQueEnsina explicacao="Nenhum indicador cadastrado para este contexto ainda." />
      )}

      {linhas && linhas.length > 0 && (
        <ul className="flex flex-col gap-3">
          {linhas.map((linha) => (
            <LinhaIndicador
              key={linha.indicador.id}
              linha={linha}
              expandido={indicadorExpandido === linha.indicador.id}
              onAlternar={() => setIndicadorExpandido((atual) => (atual === linha.indicador.id ? null : linha.indicador.id))}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
